<script setup>
// §8.5 NarrativeField — multi-line, dictation-friendly. Auto-grows; shows a
// character/quality nudge without blocking. The narrative is what an approver
// reads, so the affordance encourages specificity (§9 error voice applies when
// it is too vague — surfaced as a BlockerNote at the day level, not here).
import { ref, watch, nextTick, onBeforeUnmount } from 'vue'
import LField from './Field.vue'
import LIcon from '../base/Icon.vue'
import shared from '/shared.js'

defineProps({
	label: { type: String, default: 'Narrative' },
	hint: { type: String, default: null },
	placeholder: { type: String, default: 'What did you work on? Be specific - this is what your approver reads.' },
	rows: { type: Number, default: 3 },
})
const model = defineModel({ default: '' })
const el = ref(null)
const isListening = ref(false)

async function grow()
{
	await nextTick()
	const t = el.value
	if (!t) return
	t.style.height = 'auto'
	t.style.height = Math.max(t.scrollHeight, 60) + 'px'
}
watch(model, grow)

const SpeechRecognitionApi = window.SpeechRecognition || window.webkitSpeechRecognition
let recognition = null
let baseValue = ''

function appendNarrative(base, addition)
{
	if (!addition) return base
	return base.trim() ? `${base.trim()} ${addition}` : addition
}


function ensureRecognition()
{
	if (recognition) return recognition

	recognition = new SpeechRecognitionApi()
	recognition.continuous = true
	recognition.interimResults = true
	recognition.lang = navigator.language || 'en-US'

	recognition.onresult = (event) =>
	{
		let finalChunk = ''
		let interimChunk = ''
		for (let i = event.resultIndex; i < event.results.length; i++)
		{
			const transcript = event.results[i][0].transcript
			if (event.results[i].isFinal) finalChunk += transcript
			else interimChunk += transcript
		}
		if (finalChunk) baseValue = appendNarrative(baseValue, finalChunk)
		model.value = appendNarrative(baseValue, interimChunk)
	}

	recognition.onerror = (event) =>
	{
		isListening.value = false
		if (event.error === 'not-allowed' || event.error === 'service-not-allowed')
		{
			shared.showToast({ type: 'error', title: 'Microphone access denied', description: 'Allow microphone access in your browser to use dictation.' })
		}
		else if (event.error === 'audio-capture')
		{
			shared.showToast({ type: 'error', title: 'No microphone found', description: 'Connect a microphone to use dictation.' })
		}
		else if (event.error !== 'no-speech' && event.error !== 'aborted')
		{
			shared.showToast({ type: 'error', title: 'Dictation failed', description: 'Something went wrong while listening. Please try again.' })
		}
	}

	recognition.onend = () => { isListening.value = false }

	return recognition
}


function toggleDictation()
{
	if (!SpeechRecognitionApi)
	{
		shared.showToast({ type: 'warning', title: 'Dictation not supported', description: "Your browser doesn't support speech-to-text. Try Chrome or Edge." })
		return
	}

	if (isListening.value)
	{
		ensureRecognition().stop()
		return
	}

	baseValue = model.value || ''
	ensureRecognition().start()
	isListening.value = true
}


onBeforeUnmount(() =>
{
	if (recognition) recognition.stop()
})
</script>

<template>
	<LField :label="label" :hint="hint" v-slot="{ id }">
		<div class="wrap">
			<textarea :id="id" ref="el" v-model="model" :rows="rows" :placeholder="placeholder" class="ta" />
			<button
				class="mic" :class="{ listening: isListening }" type="button"
				:aria-label="isListening ? 'Stop dictating' : 'Dictate narrative'" @click="toggleDictation">
				<LIcon :name="isListening ? 'microphone-slash' : 'microphone'" :size="15" aria-hidden="true" />
			</button>
		</div>
	</LField>
</template>

<style scoped>
.wrap
{
	position: relative;
}

.ta
{
	font-family: var(--font-ui);
	font-size: var(--text-base);
	line-height: 1.5;
	color: var(--text);
	padding: 10px 40px 10px 12px;
	border: 1px solid var(--line);
	border-radius: var(--r);
	background: var(--surface);
	width: 100%;
	resize: vertical;
	min-height: 60px;
}

.ta::placeholder
{
	color: var(--text-3);
}

.ta:focus-visible
{
	border-color: var(--interactive);
}

.mic
{
	position: absolute;
	top: 8px;
	right: 8px;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: var(--target-min);
	height: var(--target-min);
	color: var(--icon);
	border-radius: var(--r-sm);
	border: 0;
	background: none;
	cursor: pointer;
}

.mic:hover
{
	background: var(--surface-sunken);
	color: var(--interactive-text);
}

.mic.listening
{
	color: var(--status-rejected-text);
	background: var(--surface-sunken);
	animation: mic-pulse 1.4s ease-in-out infinite;
}

@keyframes mic-pulse
{
	0%, 100% { opacity: 1; }
	50% { opacity: 0.55; }
}
</style>
